import React, {useState, useEffect} from 'react';
import {useParams, useNavigate, Link as RouterLink} from 'react-router-dom';
import {
    Box, Typography, Paper, CircularProgress, Grid, Button, Alert,
    TextField, FormControlLabel, Switch, Snackbar, Stack,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiService from '../../services/apiService';
import ImageUploader from '../../components/ImageUploader';
import {ProtectedFieldSet} from '../../context/AuthDisabledContext';
import Protected from '../../components/Protected';
import {ROLES} from '../../config/roles';
import {isRenewableConfig} from './geltConfig';

const GeltEditPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const requiredAction = isNew ? 'create' : 'update';

    const [identifier, setIdentifier] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [iconUrl, setIconUrl] = useState('');
    const [sortOrder, setSortOrder] = useState(0);
    const [minigameId, setMinigameId] = useState('');
    const [renewable, setRenewable] = useState(false);
    const [regenMax, setRegenMax] = useState('');
    const [regenAmount, setRegenAmount] = useState('');
    const [regenIntervalSec, setRegenIntervalSec] = useState('');
    const [startsFull, setStartsFull] = useState(true);
    const [minigames, setMinigames] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: ''});

    useEffect(() => {
        Promise.all([
            apiService.get('/api/minigames'),
            isNew ? Promise.resolve(null) : apiService.get(`/api/gelts/${id}`)
        ]).then(([minigamesData, geltData]) => {
            setMinigames(minigamesData);
            if (geltData) {
                setIdentifier(geltData.identifier || '');
                setName(geltData.name || '');
                setDescription(geltData.description || '');
                setIconUrl(geltData.iconUrl || '');
                setSortOrder(geltData.sortOrder ?? 0);
                setMinigameId(geltData.minigameId ?? '');
                setRenewable(isRenewableConfig(geltData));
                setRegenMax(geltData.regenMax ?? '');
                setRegenAmount(geltData.regenAmount ?? '');
                setRegenIntervalSec(geltData.regenIntervalSec ?? '');
                setStartsFull(geltData.startsFull ?? true);
            }
        })
            .catch(err => setError(err.message || 'Failed to load data'))
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        setError(null);

        const regen = {
            regenMax: renewable && regenMax !== '' ? parseInt(regenMax) : null,
            regenAmount: renewable && regenAmount !== '' ? parseInt(regenAmount) : null,
            regenIntervalSec: renewable && regenIntervalSec !== '' ? parseInt(regenIntervalSec) : null,
        };

        // A half-filled renewable config is stored happily and then classified NON-renewable by
        // every runtime: regeneration stops for everyone holding the gelt and openingAmount() drops
        // to 0, so new players open at 0 whatever "Starts Full" says. Refuse the save instead.
        if (renewable && !isRenewableConfig(regen)) {
            setError('A renewable gelt needs Max Tokens, Regeneration Amount and Regeneration '
                + 'Interval all set and greater than 0 — otherwise the server stores it as '
                + 'non-renewable and regeneration stops.');
            setIsSaving(false);
            return;
        }

        const data = {
            id: isNew ? null : parseInt(id),
            identifier, name, description, iconUrl,
            sortOrder: sortOrder ? parseInt(sortOrder) : 0,
            minigameId: minigameId || null,
            ...regen,
            startsFull,
        };

        try {
            const apiCall = isNew
                ? apiService.post('/api/gelts', data)
                : apiService.put(`/api/gelts/${id}`, data);
            await apiCall;
            setSnackbar({open: true, message: 'Gelt saved successfully!'});
            setTimeout(() => navigate('/gelts'), 800);
        } catch (err) {
            setError(err.message || 'Failed to save gelt');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;

    return (
        <Box sx={{p: 2}} component="form" onSubmit={handleSubmit}>
            <Typography variant="h4" gutterBottom>
                {isNew ? 'Create Gelt' : `Edit Gelt: ${name}`}
            </Typography>

            <Protected component={GeltEditPage} action={requiredAction} mode="disable">
                <ProtectedFieldSet>
                    <Grid container spacing={3}>
                        <Grid size={{xs: 12, md: 6}}>
                            <Stack spacing={3}>
                                <Paper sx={{p: 2}}>
                                    <Typography variant="h6" gutterBottom>Configuration</Typography>
                                    <Stack spacing={2}>
                                        <TextField required fullWidth label="Identifier" value={identifier}
                                                   onChange={e => setIdentifier(e.target.value)}/>
                                        <TextField required fullWidth label="Name" value={name}
                                                   onChange={e => setName(e.target.value)}/>
                                        <TextField fullWidth multiline rows={3} label="Description" value={description}
                                                   onChange={e => setDescription(e.target.value)}/>
                                        <TextField fullWidth type="number" label="Sort Order" value={sortOrder}
                                                   onChange={e => setSortOrder(e.target.value)}/>
                                        <FormControl fullWidth>
                                            <InputLabel>Minigame</InputLabel>
                                            <Select value={minigameId} label="Minigame"
                                                    onChange={e => setMinigameId(e.target.value)}>
                                                <MenuItem value="">Universal (no minigame)</MenuItem>
                                                {minigames.map(m => (
                                                    <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                </Paper>

                                <Paper sx={{p: 2}}>
                                    <Typography variant="h6" gutterBottom>Regeneration</Typography>
                                    <Stack spacing={2}>
                                        <FormControlLabel
                                            control={<Switch checked={renewable}
                                                             onChange={e => setRenewable(e.target.checked)}/>}
                                            label="Renewable"/>
                                        {renewable && (
                                            <>
                                                <TextField fullWidth type="number" label="Max Tokens (capacity)"
                                                           value={regenMax}
                                                           onChange={e => setRegenMax(e.target.value)}/>
                                                <TextField fullWidth type="number" label="Regeneration Amount"
                                                           value={regenAmount}
                                                           onChange={e => setRegenAmount(e.target.value)}
                                                           helperText="Tokens regenerated per interval"/>
                                                <TextField fullWidth type="number"
                                                           label="Regeneration Interval (seconds)"
                                                           value={regenIntervalSec}
                                                           onChange={e => setRegenIntervalSec(e.target.value)}
                                                           helperText="Seconds between regeneration ticks"/>
                                                <FormControlLabel
                                                    control={<Switch checked={startsFull}
                                                                     onChange={e => setStartsFull(e.target.checked)}/>}
                                                    label="Starts Full"/>
                                                {!startsFull && (
                                                    <Alert severity="info">
                                                        New players start at 0 instead of max capacity. Grant the
                                                        starting balance explicitly (e.g. via a challenge reward).
                                                    </Alert>
                                                )}
                                            </>
                                        )}
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Grid>

                        <Grid size={{xs: 12, md: 6}}>
                            <ImageUploader
                                imageUrl={iconUrl}
                                onImageUrlChange={setIconUrl}
                                uploadPath="Gelt"
                                baseFileName={name}
                                isUploadEnabled={!!name}
                            />
                        </Grid>
                    </Grid>

                    {error && <Alert severity="error" sx={{mt: 3}}>{error}</Alert>}

                    <Stack direction="row" spacing={2} sx={{mt: 3}}>
                        <Protected component={GeltEditPage} action={requiredAction}>
                            <Button type="submit" variant="contained"
                                    startIcon={isSaving ? <CircularProgress size={20}/> : <SaveIcon/>}
                                    disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Save Gelt'}
                            </Button>
                        </Protected>
                        <Button component={RouterLink} to="/gelts" variant="outlined" startIcon={<ArrowBackIcon/>}>
                            Back to List
                        </Button>
                    </Stack>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({open: false, message: ''})}
                        message={snackbar.message}
                    />
                </ProtectedFieldSet>
            </Protected>
        </Box>
    );
};

GeltEditPage.abilities = {
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
};

export default GeltEditPage;
